// Heavily inspired by https://github.com/authts/oidc-client-ts which we cannot use because it does not support our custom authorization flow
import { ref } from "vue";
import { trimEndSlash } from "@/api/http-client-helpers";
import { IFrameNavigator, IFrameWindow } from "./auth/iframe";
import { getToken } from "@/api/iam-service-api";
import { isDevelopmentEnv } from "@/shared/utils/environment-variables";
import {
    AuthResult,
    OidcSettings,
    SigninRequestCreateArgs,
    SignoutRequestCreateArgs,
    Token,
} from "@/shared/model/auth";
import { UrlUtils } from "@/shared/composables/auth/urlUtils";
import { useNgSession } from "@/shared/ng/ng-session";
import { v4 as uuidv4 } from "uuid";
import pRetry from "p-retry";
import { ngFrameworkLoaded } from "../ng/ng-integration";
import { logCustomException, logException, logMetric } from "@/shared/utils/logger/use-app-logger";

const authorityPath = `${trimEndSlash(
    environment.settings["Integrations:OpenBet:Endpoints:Auth"],
)}/iam-service/auth/realms/openengage`;
// Config for the oidc client
export const oidcSettings: OidcSettings = {
    client_id: "webapp-client", // The name of the client in Keycloak setup for this service
    client_secret: "webapp",
    redirect_uri: `${window.location.origin}${environment.sportBetsRootUrl}auth-result.html`, // Where to redirect the user to after successful (de)authentication
    response_type: "code", // Indicate the authorization code flow should be used
    scope: "account common oidc payment paymethods promotion sportsbook",
    metadata: {
        authorization_endpoint: authorityPath + "/protocol/openid-connect/auth",
        token_endpoint: authorityPath + "/protocol/openid-connect/token",
        end_session_endpoint: authorityPath + "/protocol/openid-connect/logout",
    },
    requestTimeoutInSeconds: 20,
    iframeNotifyParentOrigin: window.location.origin,
};

export default function useAuthService(settings: OidcSettings = oidcSettings) {
    const iframeNavigator = ref(new IFrameNavigator(settings));
    // Here we listen to messages coming from /auth-result.html page
    window.addEventListener(
        "message",
        async (e: MessageEvent) => {
            // Message needs to arrive from the same origin otherwise unsafe
            if (e.origin !== location.origin) return;
            if (e.data?.type === "@auth/iam-service" && e.data?.payload?.message === "AUTH_RESULT") {
                if (isDevelopmentEnv()) console.log("AuthResult message received", e.data);
                // Auth callback will trigger closure of iframe and further processing of auth response to get token (does nothing when there is no auth iframe opened)
                await callback(e.data?.payload?.data?.url);
            }
        },
        false,
    );

    // Pass NG credentials when needed
    window.addEventListener("message", async (event) => {
        // Verify the origin of the received message based on the second-level domain
        const secondLevelDomain = (host: string) => host.split(".").slice(-2).join(".");
        if (secondLevelDomain(new URL(event.origin).hostname) !== secondLevelDomain(new URL(location.origin).hostname))
            return;

        // Check if the message is requesting credentials
        if (event.data?.name !== "ngSport:sgd:getCredentials") return;

        if (isDevelopmentEnv()) console.log("Trying to post NG Credentials", event.data.name);
        await ngFrameworkLoaded();
        const session = ng_fw.getSessionDetails();
        // Retrieve credentials from ngFw
        const credentials = {
            token: session?.SessionToken,
            playerId: session?.PlayerID,
        };

        if (!credentials.token || !credentials.playerId) {
            logCustomException(
                "NGFW Credentials missing at auth fallback",
                "NG Token or Player ID missing: " + JSON.stringify(credentials),
            );
            return;
        }
        // Send the credentials back to the requester
        event.source.postMessage({
            name: "ngSport:sgd:getCredentialsResult",
            credentials,
        });

        logMetric("ng_credentials_posted", 1);
        if (isDevelopmentEnv()) console.log("NG Credentials posted successfully?", event.data.name);
    });

    // Initiate the login process
    // Default number of retries is 2 which means that if initial call fails additional 2 attempts will be made until whole promise will fail
    const login = async (token: string, playerID: string, retries: number = 2): Promise<Token | undefined> => {
        const extraQueryParams = { token, playerID };
        const authResult = await pRetry(async () => await signinSilent(extraQueryParams), {
            onFailedAttempt: (error) => {
                const message = `AuthResult (login) attempt ${error.attemptNumber} failed. There are ${error.retriesLeft} retries left.`;
                logException("auth-service", error, { message });
                if (isDevelopmentEnv()) console.log(message);
            },
            retries,
        })
            .then((response) => {
                if (response?.error)
                    logException("auth-service", response.error, { message: "AuthResult (login) failed" });
                if (isDevelopmentEnv())
                    console.log(`AuthResult (login) ${response && !response?.error ? "successful" : "failed"}`);
                return response;
            })
            .catch((error) => {
                logException("auth-service", error, { message: "AuthResult (login) failed" });
                if (isDevelopmentEnv()) console.log(error);
                return undefined;
            });
        if (!authResult || authResult?.error) {
            const { refreshSession } = useNgSession();
            refreshSession();
        }
        return authResult && !authResult?.error
            ? await pRetry(async () => await getToken(authResult), {
                  onFailedAttempt: (error) => {
                      const message = `AuthResult (login - token) attempt ${error.attemptNumber} failed. There are ${error.retriesLeft} retries left.`;
                      logException("auth-service", error, { message });
                      if (isDevelopmentEnv()) console.log(message);
                  },
                  retries,
              })
                  .then((response) => {
                      if (!response) logCustomException("auth-service", "AuthResult (login - token) failed");
                      if (isDevelopmentEnv())
                          console.log(`AuthResult (login - token) ${response ? "successful" : "failed"}`);
                      response.issued_at = Date.now();
                      return response;
                  })
                  .catch((error) => {
                      logException("auth-service", error, { message: "AuthResult (login - token) failed" });
                      if (isDevelopmentEnv()) console.log(error);
                      return undefined;
                  })
            : undefined;
    };

    // Initiate the logout process
    // Default number of retries is 2 which means that if initial call fails additional 2 attempts will be made until whole promise will fail
    const logout = async (retries: number = 2): Promise<Token | undefined> => {
        const authResult = await pRetry(async () => await signoutSilent(), {
            onFailedAttempt: (error) => {
                const message = `AuthResult (logout) attempt ${error.attemptNumber} failed. There are ${error.retriesLeft} retries left.`;
                logException("auth-service", error, { message });
                if (isDevelopmentEnv()) console.log(message);
            },
            retries,
        })
            .then((response) => {
                if (response?.error)
                    logException("auth-service", response.error, { message: "AuthResult (logout) failed" });
                if (isDevelopmentEnv())
                    console.log(`AuthResult (logout) ${response && !response?.error ? "successful" : "failed"}`);
                return response;
            })
            .catch((error) => {
                logException("auth-service", error, { message: "AuthResult (logout) failed" });
                if (isDevelopmentEnv()) console.log(error);
                return undefined;
            });
        if (!authResult || authResult?.error) {
            const { refreshSession } = useNgSession();
            refreshSession();
        }
        return undefined;
    };

    // Used on /auth-result.html page to inform auth service that login/logout finished
    const callback = async (url: string) => {
        await iframeNavigator.value.callback(url);
    };

    const createSigninRequestUrl = ({
        // mandatory
        metadata,
        client_id,
        redirect_uri,
        response_type,
        scope,
        // optional
        state,
        extraQueryParams,
    }: SigninRequestCreateArgs) => {
        const parsedUrl = new URL(metadata.authorization_endpoint);
        parsedUrl.searchParams.append("client_id", client_id);
        parsedUrl.searchParams.append("redirect_uri", redirect_uri);
        parsedUrl.searchParams.append("response_type", response_type);
        parsedUrl.searchParams.append("scope", scope);
        parsedUrl.searchParams.append("state", state || uuidv4());
        for (const [key, value] of Object.entries({ ...extraQueryParams })) {
            if (value != null) {
                parsedUrl.searchParams.append(key, value.toString());
            }
        }
        return parsedUrl.href;
    };

    const createSignoutRequestUrl = ({
        // mandatory
        metadata,
        // optional
        client_id,
        redirect_uri,
        post_logout_redirect_uri,
        state,
        extraQueryParams,
    }: SignoutRequestCreateArgs) => {
        const parsedUrl = new URL(metadata.end_session_endpoint);
        if (client_id) {
            parsedUrl.searchParams.append("client_id", client_id);
        }
        if (post_logout_redirect_uri || redirect_uri) {
            parsedUrl.searchParams.append("post_logout_redirect_uri", post_logout_redirect_uri || redirect_uri);
            if (state) {
                parsedUrl.searchParams.append("state", state);
            }
        }
        for (const [key, value] of Object.entries({ ...extraQueryParams })) {
            if (value != null) {
                parsedUrl.searchParams.append(key, value.toString());
            }
        }
        return parsedUrl.href;
    };

    const loginRequestUrl = (extraQueryParams: Record<string, string | number | boolean>, state?: string) => {
        return createSigninRequestUrl({ ...settings, extraQueryParams, state });
    };

    const logoutRequestUrl = (state: string) => {
        return createSignoutRequestUrl({ ...settings, state });
    };

    const processNavigation = async (url: string, handle: IFrameWindow, state?: string) => {
        try {
            const response = await handle.navigate({ url, state });
            const queryParams = UrlUtils.readParams(response.url);
            return queryParams
                ? ({
                      state: queryParams.get("state"),
                      session_state: queryParams.get("session_state"),
                      code: queryParams.get("code"),
                      error: queryParams.get("error") || undefined,
                      error_description: queryParams.get("error_description") || undefined,
                  } as AuthResult)
                : undefined;
        } catch (error) {
            const message = "Error during auth navigator processing... closing navigator iframe";
            logException("auth-service", error, { message });
            if (isDevelopmentEnv()) console.info(message);
            handle.close();
            throw error;
        }
    };

    // Creates hidden iframe where sign in will be processed
    const signinSilent = async (extraQueryParams: Record<string, string | number | boolean>) => {
        const iframe = await iframeNavigator.value.prepare({});
        const state = uuidv4();
        const url = loginRequestUrl(extraQueryParams, state);
        return await processNavigation(url, iframe, state);
    };

    // Creates hidden iframe where sign out will be processed
    const signoutSilent = async () => {
        const iframe = await iframeNavigator.value.prepare({});
        const state = uuidv4();
        const url = logoutRequestUrl(state);
        return await processNavigation(url, iframe, state);
    };

    return {
        login,
        logout,
        callback,
    };
}
